import React, { useEffect, useState, createContext, useContext } from 'react'
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  useTheme,
  alpha,
  Skeleton,
  Tooltip,
} from '@mui/material'
import {
  FileText,
  ThumbsUp,
  PenLine,
  CheckCircle2,
  Users,
  MoreVertical,
  ExternalLink,
  Clock,
  ArrowRight,
  Edit3,
  Bell,
} from 'lucide-react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format, parseISO } from 'date-fns'
import { fr, ar, de, it, enUS } from 'date-fns/locale'
import { backendClient } from '../../../api/backend'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TYPE_COLORS = {
  NDA: '#7c3aed',
  HR: '#ea580c',
  SERVICE: '#0284c7',
  TRAVAIL: '#ea580c',
  COMMERCIAL: '#0284c7',
  IMMOBILIER: '#059669',
  FINANCIER: '#d97706',
  TECHNOLOGIE: '#7c3aed',
  SERVICES: '#0284c7',
  GENERATE: '#7c3aed',
  TEMPLATES: '#0284c7',
  MY_TEMPLATES: '#0284c7',
  EMPTY: '#78716c',
  IMPORTED_PDF: '#ea580c',
  IMPORTED_DOCX: '#ea580c',
  IMPORTED_TXT: '#ea580c',
  IMPORTED_DOC: '#ea580c',
  IMPORT: '#ea580c',
}

function getTypeColor(type) {
  if (!type) return '#78716c'
  const upper = type.toUpperCase()
  return TYPE_COLORS[upper] || '#78716c'
}

const DATE_LOCALES = { fr, ar, de, it, en: enUS }

function formatDate(dateStr, lang = 'en') {
  if (!dateStr) return ''
  try {
    const d = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr)
    return format(d, 'dd MMM yyyy @ HH:mm', { locale: DATE_LOCALES[lang] || enUS })
  } catch {
    return ''
  }
}

// ─── Shared Data Context ──────────────────────────────────────────────────────

const ContractDataContext = createContext(null)

export const ContractDataProvider = ({ children }) => {
  const user = useSelector((state) => state.auth.user?.user)
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)

  useEffect(() => {
    if (!user?.id) return

    async function fetchData() {
      setLoading(true)
      try {
        const result = await backendClient.getLifecycleDashboard(user.id)
        setData(result)
      } catch (err) {
        console.error('Error fetching lifecycle dashboard:', err)
        setData({
          stages: {
            draft: { total: 0, my_action_count: 0 },
            in_review: { total: 0, my_action_count: 0 },
            pending_signature: { total: 0, my_action_count: 0 },
            signed: { total: 0, my_action_count: 0 },
          },
          total_contracts: 0,
          recent_work: [],
          for_me_to_approve: [],
          for_me_to_sign: [],
          sent_for_signing: [],
        })
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [user?.id])

  return (
    <ContractDataContext.Provider value={{ data, loading }}>
      {children}
    </ContractDataContext.Provider>
  )
}

export const useContractData = () => useContext(ContractDataContext)

// ─── Stage Card ───────────────────────────────────────────────────────────────

function StageCard({ icon: Icon, label, total, myActionCount, actionLabel, color, loading }) {
  const theme = useTheme()

  const colorMap = {
    default: theme.palette.text.secondary,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
    success: theme.palette.success.main,
  }
  const stageColor = colorMap[color] || theme.palette.text.secondary

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '8px' }} />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '12px',
        border: `1px solid ${alpha(stageColor, 0.2)}`,
        background: alpha(stageColor, 0.03),
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'default',
        '&:hover': {
          boxShadow: `0 4px 16px ${alpha(stageColor, 0.12)}`,
          borderColor: alpha(stageColor, 0.35),
          transform: 'translateY(-1px)',
        },
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: alpha(stageColor, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon size={18} color={stageColor} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: stageColor,
                lineHeight: 1,
                mb: 0.25,
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
              }}
            >
              {total}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 600,
                color: theme.palette.text.secondary,
                fontSize: '0.65rem',
              }}
            >
              {label}
            </Typography>
          </Box>
          {myActionCount > 0 && (
            <>
              <Box sx={{ flex: 1 }} />
              <Typography
                variant="caption"
                sx={{
                  color: stageColor,
                  fontWeight: 500,
                  fontSize: '0.72rem',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {myActionCount} {actionLabel}
              </Typography>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

// ─── Type Badge ───────────────────────────────────────────────────────────────

function TypeBadge({ type }) {
  const { t } = useTranslation()
  const color = getTypeColor(type)
  const label = type ? t(`contractCreationType.${type}`, type) : '\u2014'
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: '6px',
        background: alpha(color, 0.1),
        border: `1px solid ${alpha(color, 0.2)}`,
        flexShrink: 0,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: color,
          fontSize: '0.65rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
    </Box>
  )
}

// ─── Contract Row (Recent Work) ───────────────────────────────────────────────

function RecentContractRow({ item, onClick }) {
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const goldColor = theme.palette.primary.main

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.25,
        px: 1.5,
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background 0.15s ease',
        '&:hover': {
          background: alpha(goldColor, 0.04),
        },
        '&:not(:last-child)': {
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          background: alpha(goldColor, 0.08),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <FileText size={15} color={goldColor} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '0.82rem',
            }}
          >
            {item.title}
          </Typography>
          {item.has_collaborators && (
            <Users size={12} color={theme.palette.text.disabled} style={{ flexShrink: 0 }} />
          )}
        </Box>
      </Box>

      <TypeBadge type={item.type} />

      <Box sx={{ color: theme.palette.text.disabled, display: 'flex', flexShrink: 0 }}>
        <Edit3 size={13} />
      </Box>

      {item.last_action && (
        <Box sx={{ minWidth: 0, maxWidth: 160, display: { xs: 'none', md: 'block' } }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '0.72rem',
            }}
          >
            {t(`contractAction.${item.last_action}`, item.last_action)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem', whiteSpace: 'nowrap' }}
          >
            {formatDate(item.last_action_at || item.updated_at, i18n.language)}
          </Typography>
        </Box>
      )}

      <Box sx={{ color: theme.palette.text.disabled, flexShrink: 0 }}>
        <MoreVertical size={14} />
      </Box>
    </Box>
  )
}

// ─── Action Contract Row ──────────────────────────────────────────────────────

function ActionContractRow({ item, actionType, onClick }) {
  const theme = useTheme()
  const { t } = useTranslation()
  const goldColor = theme.palette.primary.main
  const isApprove = actionType === 'approve'
  const isSign = actionType === 'sign'
  const actionColor = isApprove
    ? theme.palette.warning.main
    : isSign
    ? theme.palette.info.main
    : null

  const progress =
    isApprove
      ? `${item.approvals_done}/${item.approvals_total}`
      : isSign || actionType === 'sent'
      ? `${item.signatures_done}/${item.signatures_total}`
      : null

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        py: 1.25,
        px: 1.5,
        borderRadius: '8px',
        transition: 'background 0.15s ease',
        '&:hover': {
          background: alpha(goldColor, 0.03),
        },
        '&:not(:last-child)': {
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '6px',
          background: alpha(goldColor, 0.07),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <FileText size={13} color={goldColor} />
      </Box>

      <Box
        onClick={onClick}
        sx={{
          flex: 1,
          minWidth: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            color: theme.palette.text.primary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.78rem',
          }}
        >
          {item.title}
        </Typography>
        {item.has_collaborators && (
          <Users size={11} color={theme.palette.text.disabled} style={{ flexShrink: 0 }} />
        )}
      </Box>

      <TypeBadge type={item.type} />

      {progress && (
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 600,
            fontSize: '0.72rem',
            flexShrink: 0,
            minWidth: 28,
            textAlign: 'right',
          }}
        >
          {progress}
        </Typography>
      )}

      {actionColor && (
        <Box
          sx={{
            px: 1.25,
            py: 0.4,
            borderRadius: '6px',
            border: `1.5px solid ${alpha(actionColor, 0.4)}`,
            background: alpha(actionColor, 0.06),
            cursor: 'pointer',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            transition: 'all 0.15s ease',
            '&:hover': {
              background: alpha(actionColor, 0.12),
              borderColor: actionColor,
            },
          }}
          onClick={onClick}
        >
          {isApprove ? (
            <ThumbsUp size={10} color={actionColor} />
          ) : (
            <PenLine size={10} color={actionColor} />
          )}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: actionColor,
              fontSize: '0.62rem',
              letterSpacing: '0.06em',
            }}
          >
            {isApprove ? t('approve') : t('sign')}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

// ─── Section Header ───────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, title, count, color, onViewAll, rightExtra }) {
  const theme = useTheme()
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 0.5,
        pb: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Icon size={15} color={color || theme.palette.text.secondary} />
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '0.82rem' }}
      >
        {title}
      </Typography>
      {count > 0 && (
        <Box
          sx={{
            px: 0.75,
            py: 0.1,
            borderRadius: '10px',
            background: alpha(color || theme.palette.text.secondary, 0.1),
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: color || theme.palette.text.secondary,
              fontSize: '0.65rem',
            }}
          >
            {count}
          </Typography>
        </Box>
      )}
      <Box sx={{ flex: 1 }} />
      {rightExtra}
      {onViewAll && (
        <Box
          onClick={onViewAll}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.4,
            cursor: 'pointer',
            color: theme.palette.primary.main,
            '&:hover': { opacity: 0.7 },
          }}
        >
          <ExternalLink size={11} />
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
            {t('view_all')}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

// ─── Loading Skeleton ─────────────────────────────────────────────────────────

function ListSkeleton({ rows = 3 }) {
  return (
    <Box sx={{ px: 1.5, py: 1 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
          <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: '8px', flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={14} />
          </Box>
          <Skeleton variant="rectangular" width={40} height={18} sx={{ borderRadius: '6px' }} />
        </Box>
      ))}
    </Box>
  )
}

// ─── Exported Section Components ──────────────────────────────────────────────

/** Stage cards row (Drafts / Approval / Signing / Done) */
export const ContractStageCards = () => {
  const { t } = useTranslation()
  const { data, loading } = useContractData()
  const stages = data?.stages || {}

  return (
    <Grid container spacing={2}>
      <Grid item xs={6} sm={3}>
        <StageCard
          icon={FileText}
          label={t('drafts') || 'Drafts'}
          total={stages.draft?.total ?? 0}
          myActionCount={0}
          color="default"
          loading={loading}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <StageCard
          icon={ThumbsUp}
          label={t('approval') || 'Approval'}
          total={stages.in_review?.total ?? 0}
          myActionCount={stages.in_review?.my_action_count ?? 0}
          actionLabel={t('for_me_to_approve') || 'For me to approve'}
          color="warning"
          loading={loading}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <StageCard
          icon={PenLine}
          label={t('signing_stage') || 'Signing'}
          total={stages.pending_signature?.total ?? 0}
          myActionCount={stages.pending_signature?.my_action_count ?? 0}
          actionLabel={t('for_me_to_sign') || 'For me to sign'}
          color="info"
          loading={loading}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <StageCard
          icon={CheckCircle2}
          label={t('done') || 'Done'}
          total={stages.signed?.total ?? 0}
          myActionCount={0}
          color="success"
          loading={loading}
        />
      </Grid>
    </Grid>
  )
}

/** Action cards row (Approve / Sign / Sent) */
export const ContractActionCards = () => {
  const { t, i18n } = useTranslation()
  const currentLang = i18n.language
  const theme = useTheme()
  const navigate = useNavigate()
  const { data, loading } = useContractData()

  const cardSx = {
    elevation: 0,
    borderRadius: '12px',
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 2px 8px rgba(0, 0, 0, 0.3)'
      : '0 2px 8px rgba(0, 0, 0, 0.04)',
    direction: currentLang === 'ar' ? 'rtl' : 'ltr',
  }

  return (
    <Grid container spacing={2}>
      {/* For me to approve */}
      <Grid item xs={12} md={4}>
        <Card elevation={0} sx={cardSx}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <SectionHeader
                icon={ThumbsUp}
                title={t('for_me_to_approve') || 'For me to approve'}
                count={data?.for_me_to_approve?.length ?? 0}
                color={theme.palette.warning.main}
                onViewAll={() => navigate('/contract?tab=in_review')}
              />
            </Box>
            {loading ? (
              <ListSkeleton rows={2} />
            ) : data?.for_me_to_approve?.length > 0 ? (
              <Box sx={{ pb: 1 }}>
                {data.for_me_to_approve.map((item) => (
                  <ActionContractRow
                    key={item.id}
                    item={item}
                    actionType="approve"
                    onClick={() => navigate(`/contract/${item.id}`)}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ py: 2.5, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.disabled, fontSize: '0.78rem' }}
                >
                  {t('no_approvals_pending') || 'No approvals pending'}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* For me to sign */}
      <Grid item xs={12} md={4}>
        <Card elevation={0} sx={cardSx}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <SectionHeader
                icon={PenLine}
                title={t('for_me_to_sign') || 'For me to sign'}
                count={data?.for_me_to_sign?.length ?? 0}
                color={theme.palette.info.main}
                onViewAll={() => navigate('/contract?tab=to_sign')}
              />
            </Box>
            {loading ? (
              <ListSkeleton rows={2} />
            ) : data?.for_me_to_sign?.length > 0 ? (
              <Box sx={{ pb: 1 }}>
                {data.for_me_to_sign.map((item) => (
                  <ActionContractRow
                    key={item.id}
                    item={item}
                    actionType="sign"
                    onClick={() => navigate(`/contract/${item.id}`)}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ py: 2.5, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.disabled, fontSize: '0.78rem' }}
                >
                  {t('no_signatures_pending') || 'No signatures pending'}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Sent for signing */}
      <Grid item xs={12} md={4}>
        <Card elevation={0} sx={cardSx}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <SectionHeader
                icon={ArrowRight}
                title={t('sent_for_signing') || 'Sent for signing'}
                count={data?.sent_for_signing?.length ?? 0}
                color={theme.palette.secondary.main}
                onViewAll={() => navigate('/contract?tab=pending_signature')}
                rightExtra={
                  data?.sent_for_signing?.length > 0 ? (
                    <Tooltip title={t('notify_undone') || 'Notify unsigned'}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1,
                          py: 0.3,
                          borderRadius: '6px',
                          border: `1px solid ${alpha(theme.palette.text.secondary, 0.25)}`,
                          cursor: 'pointer',
                          mr: 1,
                          '&:hover': { background: alpha(theme.palette.text.secondary, 0.06) },
                        }}
                      >
                        <Bell size={11} color={theme.palette.text.secondary} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                            fontSize: '0.62rem',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {t('notify_undone')} ({data.sent_for_signing.length})
                        </Typography>
                      </Box>
                    </Tooltip>
                  ) : null
                }
              />
            </Box>
            {loading ? (
              <ListSkeleton rows={2} />
            ) : data?.sent_for_signing?.length > 0 ? (
              <Box sx={{ pb: 1 }}>
                {data.sent_for_signing.map((item) => (
                  <ActionContractRow
                    key={item.id}
                    item={item}
                    actionType="sent"
                    onClick={() => navigate(`/contract/${item.id}`)}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ py: 2.5, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.disabled, fontSize: '0.78rem' }}
                >
                  {t('no_sent_for_signing') || 'No contracts sent for signing'}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )
}

/** Recent work card (standalone, no outer Grid) */
export const ContractRecentWork = () => {
  const { t, i18n } = useTranslation()
  const currentLang = i18n.language
  const theme = useTheme()
  const navigate = useNavigate()
  const { data, loading } = useContractData()

  const cardSx = {
    elevation: 0,
    borderRadius: '12px',
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 2px 8px rgba(0, 0, 0, 0.3)'
      : '0 2px 8px rgba(0, 0, 0, 0.04)',
    direction: currentLang === 'ar' ? 'rtl' : 'ltr',
    height: '100%',
  }

  return (
    <Card elevation={0} sx={cardSx}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <SectionHeader
            icon={Clock}
            title={t('my_recent_work') || 'My recent work'}
            count={0}
            onViewAll={() => navigate('/contract?tab=recent')}
          />
        </Box>

        {loading ? (
          <ListSkeleton rows={5} />
        ) : data?.recent_work?.length > 0 ? (
          <Box sx={{ pb: 1 }}>
            {data.recent_work.map((item) => (
              <RecentContractRow
                key={item.id}
                item={item}
                onClick={() => navigate(`/contract/${item.id}`)}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
            <FileText size={28} color={theme.palette.text.disabled} />
            <Typography
              variant="body2"
              sx={{ mt: 1, color: theme.palette.text.disabled, fontSize: '0.8rem' }}
            >
              {t('no_contracts_yet') || 'No contracts yet'}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
